import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useI18n } from '../i18n'
import { useSongStore } from '../store/useSongStore'
import { OnlineIndicator } from '../components/offline/OnlineIndicator'

export function PlaylistPage() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { playlistId } = useParams<{ playlistId: string }>()
  const {
    songs,
    playlists,
    fetchSongs,
    fetchPlaylists,
    renamePlaylist,
    deletePlaylist,
    removeSongFromPlaylist,
    reorderPlaylistSong,
  } = useSongStore()

  const [editingName, setEditingName] = useState(false)

  useEffect(() => {
    void fetchPlaylists()
    void fetchSongs()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const playlist = playlists.find((p) => p.id === playlistId)
  if (!playlist) {
    return (
      <div className="flex min-h-full flex-col">
        <header className="flex items-center gap-2 px-4 py-3">
          <button
            className="flex items-center gap-2 text-text-muted hover:text-text"
            onClick={() => navigate('/songs')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"/></svg>
            <span className="text-lg font-bold text-text">{t('songbook.playlists')}</span>
          </button>
          <OnlineIndicator />
        </header>
        <main className="flex-1 px-4 py-10 text-center text-text-muted">
          {t('general.loading')}
        </main>
      </div>
    )
  }

  const playlistSongs = playlist.songIds
    .map((id) => songs.find((s) => s.id === id))
    .filter(Boolean) as typeof songs

  async function handleDelete() {
    if (confirm(t('songbook.deletePlaylistConfirm'))) {
      await deletePlaylist(playlist!.id)
      navigate('/songs')
    }
  }

  return (
    <div className="flex min-h-full flex-col">
      <header className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            className="flex items-center gap-2 text-text-muted hover:text-text"
            onClick={() => navigate('/songs')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"/></svg>
          </button>
          {editingName ? (
            <input
              type="text"
              defaultValue={playlist.name}
              autoFocus
              className="rounded border border-primary bg-surface px-2 py-1 text-lg font-bold text-text focus:outline-none"
              onBlur={(e) => {
                const val = e.target.value.trim()
                if (val && val !== playlist.name) renamePlaylist(playlist.id, val)
                setEditingName(false)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                if (e.key === 'Escape') setEditingName(false)
              }}
            />
          ) : (
            <span
              className="cursor-pointer text-lg font-bold text-text"
              onDoubleClick={() => setEditingName(true)}
            >
              {playlist.name}
            </span>
          )}
          <OnlineIndicator />
        </div>
        <div className="flex items-center gap-1">
          {playlistSongs.length > 0 && (
            <button
              className="rounded bg-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary/80"
              onClick={() => navigate(`/playlist/${playlistId}/live`)}
            >
              PLAY
            </button>
          )}
          <button
            className="rounded p-1.5 text-text-muted hover:text-text"
            onClick={() => setEditingName(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z"/></svg>
          </button>
          <button
            className="rounded p-1.5 text-text-muted hover:text-error"
            onClick={handleDelete}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
          </button>
        </div>
      </header>

      <main className="flex-1 px-4 pb-4">
        {playlistSongs.length === 0 ? (
          <p className="py-10 text-center text-text-muted">{t('songbook.noPlaylists')}</p>
        ) : (
          <div className="space-y-2">
            {playlistSongs.map((song, idx) => (
              <div
                key={song.id}
                className="flex items-center gap-2 rounded-lg border border-surface-lighter bg-surface-light p-3"
              >
                {/* Reorder buttons */}
                <div className="flex shrink-0 flex-col gap-0.5">
                  <button
                    className="rounded p-0.5 text-text-muted hover:text-text disabled:opacity-30"
                    disabled={idx === 0}
                    onClick={() => reorderPlaylistSong(playlist.id, song.id, 'up')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m18 15-6-6-6 6"/></svg>
                  </button>
                  <button
                    className="rounded p-0.5 text-text-muted hover:text-text disabled:opacity-30"
                    disabled={idx === playlistSongs.length - 1}
                    onClick={() => reorderPlaylistSong(playlist.id, song.id, 'down')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6"/></svg>
                  </button>
                </div>

                {/* Song info */}
                <div
                  className="min-w-0 flex-1 cursor-pointer"
                  onClick={() => navigate(`/playlist/${playlist.id}/live?start=${idx}`)}
                >
                  <h3 className="truncate text-base font-semibold text-text">
                    {song.title || t('songbook.untitled')}
                  </h3>
                </div>

                {/* Remove from playlist */}
                <button
                  className="shrink-0 rounded p-1.5 text-text-muted hover:text-error"
                  onClick={() => removeSongFromPlaylist(playlist.id, song.id)}
                  title={t('songbook.removeFromPlaylist')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
